/* eslint-disable react/require-default-props */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  DataGrid,
  DataGridMobile,
  DataGridMethods,
  ErrorPlaceholder,
  LoadingPlaceholder,
  NoDataPlaceholder,
  Column
} from "@cadmakers/data-grid-v2";
import { isPhone } from "@cadmakers-ui/core";
import { useHistory } from "react-router";
import {
  FeedbackCategory,
  FeedbackVote,
  FeedbackSortOption
} from "../../../Constants/feedbackEnums";
import { SeeMoreLink } from "./FeedbackList";
import { LoadingDisplay, ErrorDisplay, ApiRequest, IAppState } from "../../../../Common";
import { Feedback } from "../../../Models/Feedback";
import { feedbackVoteService } from "../../../Services/FeedbackVoteService";
import {
  getFeedbacks,
  unloadSearch,
  setLoading,
  setErrored,
  loadTransitionsIntoList,
  setTransitionsLoading
} from "../../../Actions/FeedbackListActions";
import { endpoints } from "../../..";
import ResponseHandler from "../../../../App/ResponseHandler";
import { ResponseResult } from "../../../../Common/Models/ResponseResult";
import { SearchContext } from "../../../../Layout/Models/SearchContext";
import { feedbackService } from "../../../Services";
import styles from "../../../Styles/FeedbackListContainer.module.scss";
import { useTags } from "../../../../Tags";
import { User } from "../../../../User";
import { FeedbackRowData, FeedbackRowTitle } from "./Types/FeedbackRowData";
import { feedbackListDataService } from "./Services/FeedbackListDataService";
import { useFeedbackList } from "./Hooks/useFeedbackList";
import { DueDateDisplayModal } from "../DueDateDisplay/DueDateDisplayModal";
import { FeedbackListContext } from "./FeedbackListContext";

type Props = {
  // this is a unique ID for this list, required
  listId: string;

  // props for displaying the list
  seeMoreLink?: SeeMoreLink;
  treeView?: boolean;
  reorderable?: boolean;
  maxPerPage?: number;
  paging?: boolean;
  enableSearch?: boolean;
  datagridHeight?: string;
  fullscreen?: boolean;
  virtualizeDisabled?: boolean;
  enableDownload?: boolean;
  reloadOnRender?: Boolean; // For search bar
  setReloadFeedback?: (reload: Boolean, context?: SearchContext, query?: string) => void;

  // props for the search
  maxNumToGet?: number;
  sort?: FeedbackSortOption;
  isAscending?: boolean;
  searchQuery?: string;
  username?: string;
  shortname?: string;
  context?: SearchContext;
  startDate?: Date;
  endDate?: Date;
  tags?: number[];
  assignedUsers?: number[];
  selectedCategories?: FeedbackCategory[];
  selectedApplications?: number[];
  selectedTopics?: number[];
};

const FeedbackListContainer = (props: Props) => {
  const dispatch = useDispatch();
  const ref = useRef<DataGridMethods>(null);
  const history = useHistory();

  // state
  const [tableData, setTableData] = useState([] as FeedbackRowData[]);
  const [hasSavedFilters, setHasSavedFilters] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [feedbackEditingDueDateFor, setFeedbackEditingDueDateFor] = useState<string | null>(null);
  const [currentEditedDueDate, setCurrentEditedDueDate] = useState<Date | undefined>();
  const toggleModal = () => setIsModalOpen(!isModalOpen);

  // redux state
  const { list: feedbackListState, updateListItem, updateSavedFilters } = useFeedbackList(
    props.listId
  );

  const user = useSelector((state: IAppState) => state.oidc.user);
  const tags = useTags();

  const isCMUser =
    user && user.profile.companyName && user.profile.companyName.toLowerCase() === "cadmakers";

  // startup logic
  useEffect(() => {
    return () => {
      // unload the search when the list is unloaded
      dispatch(unloadSearch({ listId: props.listId }));
    };
  }, []);
  // dispatch the actions to load the feedback
  const reload = () => {
    dispatch(setLoading({ ...props }));
    dispatch(getFeedbacks({ ...props }));
  };

  const reloadOnSearch = () => {
    if (props.setReloadFeedback) {
      reload();
      props.setReloadFeedback(false);
    }
  };

  useEffect(() => {
    reloadOnSearch();
  }, [props.reloadOnRender]);

  useEffect(() => {
    reload();
  }, [props.selectedCategories, props.selectedApplications, props.selectedTopics]);

  const onVoteFeedback = (feedbackCode: string, vote: FeedbackVote) => {
    if (!feedbackListState.items) {
      return;
    }

    // get this feedback from the props
    const thisFeedback = { ...feedbackListState.items.filter(f => f.code === feedbackCode)[0] };

    // update vote count
    thisFeedback.voteCount = feedbackVoteService.getNewCountAfterVote(thisFeedback, vote);

    // update logged in user's vote
    thisFeedback.loggedInUserVote = vote.toString();

    // dispatch an action
    dispatch(updateListItem({ ...props, updatedFeedback: thisFeedback }));
  };

  const onFeedbackDueDateChange = (feedbackCode: string, newDueDate: Date | undefined) => {
    if (!feedbackListState.items) {
      return;
    }

    // get this feedback from the props
    const thisFeedback = { ...feedbackListState.items.filter(f => f.code === feedbackCode)[0] };

    // update vote count
    thisFeedback.dueDate = newDueDate ? newDueDate.toISOString() : undefined;

    // dispatch an action
    dispatch(updateListItem({ ...props, updatedFeedback: thisFeedback }));

    setIsModalOpen(false);
  };

  const onFeedbackStateChange = (feedbackCode: string) => {
    // get this feedback from the server

    ApiRequest.fromPath(endpoints.feedback.getByCode(feedbackCode))
      .withAuthHeader()
      .get()
      .then((response: Response) => {
        ResponseHandler.handle(
          response,
          (result: ResponseResult) => {
            // dispatch an action to update it
            updateListItem({ ...props, updatedFeedback: result.payload });
          },
          (result: ResponseResult) => {
            setErrored({
              ...props,
              errorCode: response.status,
              errorMessage: result.error.message
            });
          },
          () => {
            setErrored({
              ...props,
              errorCode: response.status,
              errorMessage: "An unknown error has occured"
            });
          }
        );
      });
  };

  const onFeedbackAssignedUsersChange = (feedback: Feedback, users: User[]) => {
    const updatedFeedback = feedback;
    updatedFeedback.assignedTo = users.map(u => ({
      id: u.id,
      username: u.username,
      name: u.name
    }));
    updateListItem({ ...props, updatedFeedback });
  };

  const onMove = (feedback: Feedback) => {
    reload();
  };

  const loadTransitions = () => {
    // trigger loading the transitions
    dispatch(setTransitionsLoading({ listId: props.listId }));
    dispatch(
      loadTransitionsIntoList({
        listId: props.listId,
        feedbackCodes: feedbackListState.items ? feedbackListState.items.map(i => i.code) : []
      })
    );
  };

  // loads the feedbacks if they have not been loaded
  const loadIfNeeded = (prevProps: Props) => {
    if (!tags.loadingState.isLoaded) {
      return;
    }

    if (
      !feedbackListState.isLoaded &&
      !feedbackListState.isLoading &&
      !feedbackListState.isErrored
    ) {
      reload();
    }
  };

  const onClickEditDueDate = (feedbackCode: string, currentDueDate: Date | undefined) => {
    setFeedbackEditingDueDateFor(feedbackCode);
    setCurrentEditedDueDate(currentDueDate);
    setIsModalOpen(true);
  };

  const mapFeedbacksToTableData = (): FeedbackRowData[] => {
    if (
      !feedbackListState ||
      !feedbackListState.items ||
      !feedbackListState.isLoaded ||
      !tags.loadingState.isLoaded
    ) {
      return [];
    }

    if (props.treeView) {
      return feedbackListDataService.mapFeedbacksToTreeData(
        feedbackListState.items,
        tags.tags,
        feedbackListState.areTransitionsLoaded,
        onClickEditDueDate
      );
    }

    return feedbackListDataService.mapFeedbacksToFlatData(
      feedbackListState.items,
      tags.tags,
      feedbackListState.areTransitionsLoaded,
      onClickEditDueDate
    );
  };

  const getRenderPlaceholder = () => {
    if (!feedbackListState.isLoaded) {
      if (feedbackListState.isErrored) {
        return (
          <ErrorPlaceholder
            message={`Error code: ${feedbackListState.errorCode} (${feedbackListState.errorMessage})`}
          />
        );
      }
      return <LoadingPlaceholder />;
    }

    if (feedbackListState.items && feedbackListState.items.length === 0) {
      return <NoDataPlaceholder message={<span>There are no results to show</span>} />;
    }

    return null;
  };

  const onRowClickMobile = (rowData: any) => {
    const castValue = rowData.title as FeedbackRowTitle;
    history.push(castValue.url);
  };

  const downloadCSV = () => {
    if (ref.current) {
      ref.current.exportCSV();
    }
  };

  useEffect(() => {
    // TODO add dependencies for load check
    loadIfNeeded(props);
  }, [
    tags.loadingState.isLoaded,
    feedbackListState.isLoaded,
    feedbackListState.isErrored,
    feedbackListState.isLoading
  ]);

  useEffect(() => {
    // check if we need to load transitions
    if (
      feedbackListState.isLoaded &&
      !feedbackListState.areTransitionsLoaded &&
      !feedbackListState.areTransitionsLoading
    ) {
      // load the transitions
      loadTransitions();
    }
  }, [
    feedbackListState.isLoaded,
    feedbackListState.areTransitionsLoaded,
    feedbackListState.areTransitionsLoading
  ]);

  // map feedback to table data when the list is updated
  useEffect(() => {
    setTableData(mapFeedbacksToTableData());
  }, [
    feedbackListState.isLoaded,
    tags.loadingState.isLoaded,
    feedbackListState.areTransitionsLoaded,
    feedbackListState.items
  ]);

  const enableSearch = props.enableSearch === undefined ? true : props.enableSearch;

  const isReorderable = props.reorderable === undefined ? true : props.reorderable;

  const maxPerPage = props.maxPerPage ? props.maxPerPage : 50;

  const columns = useMemo(() => {
    return feedbackListDataService.getColumnDefinitions({
      enableSearch,
      isCMUser,
      styles,
      onVoteFeedback,
      onFeedbackAssignedUsersChange,
      onStateChange: onFeedbackStateChange,
      onDueDateChange: onFeedbackDueDateChange,
      onMove
    });
  }, [enableSearch, isCMUser]);

  // Automatically updates the filter to exclude closed states when the datagrid reloads if there are no saved filters

  const excludeClosedStates = () => {
    if (!enableSearch || !feedbackListState.isLoaded || !feedbackListState.items) {
      return;
    }

    const nonClosedStateNames = feedbackListState.items
      .filter(i => !i.workflowState.isClosed)
      .map(i => i.workflowState.name);

    // return only unique names
    const uniqueNames = Array.from(new Set(nonClosedStateNames));

    // set the filter
    if (ref.current && ref.current.setFilter) {
      ref.current.setFilter(columns.find(x => x.accessor === "status")!.accessor, {
        tags: uniqueNames,
        operator: "or"
      });
      updateSavedFilters({ listId: props.listId, filters: ref.current.exportFilters() });
    }
  };

  useEffect(() => {
    excludeClosedStates();
  }, [feedbackListState.isLoaded]);

  const updateCurrentFilters = () => {
    if (feedbackListState.savedFilters && feedbackListState.isInitialFilterSaved) {
      feedbackListState.savedFilters.forEach(f => {
        if (ref.current && f.filter) {
          ref.current.setFilter(f.id, f.filter);
        }
      });
      updateSavedFilters({ listId: props.listId, filters: feedbackListState.savedFilters });
    }
  };

  useEffect(() => {
    // update the filters when the saved filters change
    updateCurrentFilters();
  }, [feedbackListState.savedFilters]);

  useEffect(() => {
    // on list unmount, save the filters for the list
    return () => {
      // save the filters for the list
      if (ref.current && ref.current.exportFilters && enableSearch && !hasSavedFilters) {
        updateSavedFilters({ listId: props.listId, filters: ref.current.exportFilters() });
        setHasSavedFilters(true);
      }
    };
  }, []);

  const reloadOnMove = (feedback: Feedback) => {
    reload();
  };

  let height = "";
  if (props.fullscreen) {
    height = "calc(100vh - (130px))";
  } else height = props.datagridHeight ? props.datagridHeight : "500px";

  return (
    <>
      {isPhone ? (
        <div style={{ height, flexGrow: 1 }}>
          <DataGridMobile
            ref={ref as any}
            columns={columns}
            data={tableData}
            exportCsv={props.enableDownload}
            renderPlaceholder={getRenderPlaceholder}
            rowHeaderColumnId="title"
            onRowClick={onRowClickMobile}
          />
        </div>
      ) : (
        <div style={{ height, minWidth: "200px" }}>
          <FeedbackListContext.Provider value={{ reloadOnMove }}>
            <DataGrid
              ref={ref as any}
              data={tableData}
              treeData={props.treeView ?? false}
              columns={columns}
              exportCsv={props.enableDownload}
              renderPlaceholder={getRenderPlaceholder}
              disableVirtualization={props.virtualizeDisabled}
              paging={props.paging && !props.treeView}
              pagingSizes={[
                { value: 25 },
                { value: 50, default: true },
                { value: 75 },
                { value: 100 },
                { value: -1 }
              ]}
            />
          </FeedbackListContext.Provider>
        </div>
      )}
      {feedbackEditingDueDateFor && (
        <DueDateDisplayModal
          feedbackCode={feedbackEditingDueDateFor}
          dueDate={currentEditedDueDate}
          isOpen={isModalOpen}
          toggleModal={toggleModal}
          onChange={onFeedbackDueDateChange}
        />
      )}
    </>
  );
};

export default FeedbackListContainer;
